import React, { useState } from "react";
import { Table, Tag, Dropdown, Button } from "antd";
import { useSelector } from "react-redux";
import EditTagModal from "./EditTagModal";
import DeleteTagModal from "./DeleteTagModal";
import { TagIconPreview } from "./IconPicker";

export default function ListView({ searchText }) {
  const { tagsList, selectedScope } = useSelector((state) => ({
    tagsList: state.tags?.tagsList || [],
    selectedScope: state.tags?.selectedScope || "all",
  }));

  const [editTag, setEditTag] = useState(null);
  const [deleteTagInfo, setDeleteTagInfo] = useState(null);

  // Apply scope and search filter
  const filteredTags = tagsList.filter((tag) => {
    const tagScope = (tag.scope || "global").toLowerCase();
    const matchesScope = selectedScope === "all" || tagScope === selectedScope;
    const tagSlug = (tag.slug || tag.code || tag.name || "").toLowerCase();
    const matchesSearch =
      !searchText ||
      tag.name.toLowerCase().includes(searchText.toLowerCase()) ||
      tagSlug.includes(searchText.toLowerCase());
    return matchesScope && matchesSearch;
  });

  const columns = [
    {
      title: "Tag",
      dataIndex: "name",
      key: "name",
      align: "left",
      width: 180,
      render: (_, record) => (
        <TagIconPreview
          icon={record.icon}
          badgeColor={record.color || record.badgeColor || "#0d6efd"}
          name={record.name}
        />
      ),
    },
    {
      title: "Slug",
      dataIndex: "slug",
      key: "slug",
      align: "left",
      width: 160,
      render: (_, record) => {
        const rawSlug = (record.slug || record.code || "").trim();
        const rawName = (record.name || "").trim();
        const displaySlug = rawSlug || rawName.toLowerCase().replace(/[^a-z0-9]+/g, "-") || "-";
        return <code className="bg-light px-2 py-1 rounded text-dark fw-semibold">{displaySlug}</code>;
      },
    },
    {
      title: "Scope",
      dataIndex: "scope",
      key: "scope",
      align: "center",
      width: 120,
      render: (scope) => {
        const val = (scope || "global").toUpperCase();
        let color = "cyan";
        if (val === "SKILL") color = "blue";
        if (val === "COURSE") color = "purple";
        if (val === "PRODUCT") color = "geekblue";
        if (val === "DEAL") color = "magenta";
        if (val === "BLOG") color = "orange";
        if (val === "CATEGORY") color = "green";
        return (
          <Tag color={color} style={{ fontWeight: 600, borderRadius: "12px", padding: "2px 10px" }}>
            {val}
          </Tag>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      align: "left",
      render: (desc) => (
        <span className="text-secondary small">
          {desc && desc.trim() ? desc : "-"}
        </span>
      ),
    },
    {
      title: "Usage",
      dataIndex: "courseCount",
      key: "courseCount",
      align: "center",
      width: 120,
      render: (count) => (
        <Tag color="geekblue" style={{ borderRadius: "10px" }}>
          {count || 0} items
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      align: "center",
      width: 110,
      render: (_, record) => {
        const active = record.isActive !== undefined ? Boolean(record.isActive) : record.status !== "inactive";
        return (
          <Tag color={active ? "success" : "red"} style={{ borderRadius: "10px", fontWeight: 600 }}>
            {active ? "ACTIVE" : "INACTIVE"}
          </Tag>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 90,
      fixed: "right",
      render: (_, record) => (
        <Dropdown
          dropdownRender={() => (
            <div
              className="dropdown-menu dropdown-menu-right show"
              style={{ display: "block", position: "static", margin: 0, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
            >
              <button
                className="dropdown-item d-flex align-items-center gap-2"
                onClick={() => setEditTag(record)}
              >
                <i className="la la-edit text-primary" /> Edit Tag
              </button>
              <button
                className="dropdown-item d-flex align-items-center gap-2 text-danger"
                onClick={() => setDeleteTagInfo(record)}
              >
                <i className="la la-trash text-danger" /> Delete Tag
              </button>
            </div>
          )}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button type="text" shape="circle" icon={<i className="material-icons">more_vert</i>} />
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <div className="table-responsive">
        <Table
          className="table-striped"
          columns={columns}
          dataSource={filteredTags}
          rowKey={(record) => record._id || record.id || record.slug}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          scroll={{ x: "max-content" }}
        />
      </div>

      {editTag && (
        <EditTagModal
          visible={!!editTag}
          info={editTag}
          onCancel={() => setEditTag(null)}
        />
      )}

      {deleteTagInfo && (
        <DeleteTagModal
          visible={!!deleteTagInfo}
          info={deleteTagInfo}
          onCancel={() => setDeleteTagInfo(null)}
        />
      )}
    </>
  );
}
